import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useState } from 'react';
import { Col } from 'reactstrap';
import Swal from 'sweetalert2';
import dayjs from 'dayjs';
import arrow from '../../../assets/images/arrow.png';
import { postDataAgnostic } from '../../../Services/dataApi';
import { ordinal_suffix_of } from '../../../SortableList';
import { CenterRow, CustomTable, TDPadding, PreferenceNumber } from './InterviewDateConfirm.styles';
import { CustomButton } from './InterviewDateSelection.styles';

const InterviewDateConfirm = ({
  changeStep,
  clientID,
  dateOfInterview,
  demo,
  getCandidate,
  hasMadeSelection,
  hasPreferenceOrderWithNoInterviewDate,
  headerText,
  interviewDate,
  isScheduled,
  listArray,
  selectedDates = [], // New prop for Redux-free selected dates
  modificationConfirmed,
  setError,
  setHasInterviewDate,
  setHasNoChoicesToMake,
  setHasPermission,
  setModificationConfirmed,
  singleDateChoice,
}) => {
  const [isSending, setIsSending] = useState(false);
  const userHasNoPhoto = false;
  const [displayPhotoLink, setDisplayPhotoLink] = useState(false);

  // Use selectedDates when available (Redux-free mode), fallback to Redux listArray
  const effectiveListArray = selectedDates.length > 0 ? selectedDates : listArray;

  useEffect(() => {
    if (hasMadeSelection) {
      setDisplayPhotoLink(true);
    } else {
      setDisplayPhotoLink(false);
    }
  }, [headerText]);

  const sendRequest = useCallback(async () => {
    const selectedDates = sortData(effectiveListArray);
    const AccessID = localStorage.getItem('AccessID');

    postDataAgnostic('candidate/interviewDates', { clientID, AccessID }, { selectedDates })
      .then((results) => {
        if (results.data.DateLocked && results.data.DateLocked === true) {
          Swal.fire({
            title: 'Date Locked',
            text: `It appears you're already schedule for a date and this date is locked. If this seems like a mistake, please contact your coordinator`,
            icon: 'warning',
          }).then(() => {
            window.location.reload();
          });
        } else if (results.data.error === false) {
          window.location.reload();
        } else if (results.data.permissions === false) {
          setHasPermission(false);
        } else {
          setError(true);
        }
      })
      .catch(() => {
        setError(true);
      });
  }, [isSending, effectiveListArray]);

  const executeModificationClick = async () => {
    setHasInterviewDate(false);
    setHasNoChoicesToMake(false);

    if (dateOfInterview || hasMadeSelection) {
      setTimeout(() => {
        getCandidate(true);
      }, 500);
    } else {
      changeStep(false, 1);
    }
  };

  const handleModifyClick = async (e) => {
    e.preventDefault();

    if (modificationConfirmed === false) {
      Swal.fire({
        title: 'Caution!',
        text:
          'If you have been registered for an interview, making changes to your selection may result in forfeiting your current spot. Are you sure you want to proceed?',
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: 'red',
        confirmButtonText: `Continue Modification`,
      }).then((result) => {
        if (result.value) {
          setModificationConfirmed(true);
          executeModificationClick();
        }
      });
    } else {
      executeModificationClick();
    }
  };

  const sortData = (arrayList) => {
    if (!arrayList || arrayList.length === 0) return [];

    let itemsToSort = arrayList;

    if (arrayList.length === 1) {
      itemsToSort = [{ ...arrayList[0], preference: 0, preferenceOrder: 0 }];
    }

    // Determine if we're in new Redux-free flow or old Redux flow
    // New flow: selectedDates is populated (user made selections via our new component)
    // Old flow: selectedDates is empty, using listArray from Redux
    const isNewFlow = selectedDates.length > 0;

    let selectedItems;
    if (isNewFlow) {
      // New flow: selectedDates array contains only selected items
      selectedItems = itemsToSort;
    } else {
      // Old flow or initial load: filter for items marked as selected
      selectedItems = itemsToSort.filter((item) => item.selected || item.isSelected);
    }

    const sortedItems = selectedItems.sort((a, b) => {
      // Use preferenceOrder first, then preference as fallback
      const aPreference =
        a.preferenceOrder !== null && a.preferenceOrder !== undefined
          ? a.preferenceOrder
          : a.preference !== null && a.preference !== undefined
          ? a.preference
          : 999; // Put items without preference at the end

      const bPreference =
        b.preferenceOrder !== null && b.preferenceOrder !== undefined
          ? b.preferenceOrder
          : b.preference !== null && b.preference !== undefined
          ? b.preference
          : 999;

      return aPreference - bPreference;
    });

    return sortedItems;
  };

  const simulateSending = () => {
    Swal.fire(
      `Your selection has been submitted (not really)`,
      `If this was a real invitation, the selection would be sent to the corresponding department.`,
    );
  };

  const showSingleDateWarning =
    sortData(effectiveListArray).length === 1 &&
    !dateOfInterview &&
    !hasPreferenceOrderWithNoInterviewDate &&
    !singleDateChoice;

  const showModifyButton = (interviewDate && !interviewDate.DateLocked) || !dateOfInterview;

  const showConfirmButton = !dateOfInterview && !hasMadeSelection;

  return (
    <Col>
      <CenterRow style={{ padding: '15px' }}>
        {displayPhotoLink && userHasNoPhoto && (
          <h6 style={{ color: 'red' }}>
            There's one more thing you need to do! Upload a photo of yourself by clicking{' '}
            <span>
              <a href={`https://portal.rezrate.com/candidate/photo/${clientID}`} target="_blank" rel="noopener noreferrer">
                here.
              </a>
            </span>
          </h6>
        )}

        <CustomTable>
          <thead>
            <tr>
              <th></th>
              {!dateOfInterview && !singleDateChoice && !isScheduled && <th>Choice</th>}
              <th>Date</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {dateOfInterview && (
              <tr>
                <TDPadding />
                <td>
                  <div>{dayjs(dateOfInterview.DateOfInterview).format('dddd, MMMM DD, YYYY')}</div>
                  {dateOfInterview.Label && (
                    <span>
                      <br />
                      {dateOfInterview.Label}
                    </span>
                  )}
                </td>
                <TDPadding />
              </tr>
            )}

            {hasMadeSelection &&
              sortData(effectiveListArray).map((item, i) => (
                <tr key={item.pk_interviewDate}>
                  <TDPadding />
                  {!dateOfInterview && (
                    <td>
                      <PreferenceNumber>{ordinal_suffix_of(i + 1)}</PreferenceNumber>
                    </td>
                  )}
                  <td>
                    <div>{dayjs(item.DateOfInterview).format('dddd, MMMM DD, YYYY')}</div>
                    {item.Label && <span>{item.Label}</span>}
                  </td>
                  <TDPadding />
                </tr>
              ))}

            {!hasMadeSelection &&
              sortData(effectiveListArray).map((item, i) => (
                <tr key={item.pk_interviewDate}>
                  <TDPadding />
                  {!dateOfInterview && !singleDateChoice && (
                    <td>
                      <PreferenceNumber>{ordinal_suffix_of(i + 1)}</PreferenceNumber>
                    </td>
                  )}
                  <td>
                    <div>{dayjs(item.DateOfInterview).format('dddd, MMMM DD, YYYY')}</div>
                    {item.Label && <span>{item.Label}</span>}
                  </td>
                  <TDPadding />
                </tr>
              ))}
          </tbody>
        </CustomTable>
      </CenterRow>
      {showSingleDateWarning && (
        <div style={{ display: 'flex', justifyContent: 'space-evenly', flexDirection: 'column' }}>
          <h4 style={{ color: 'var(--rr-ink)' }}>You selected only one date. Did you know you can select multiple options?</h4>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <p style={{ marginBottom: '5px', color: 'var(--rr-ink)' }}>
              Click{' '}
              <span className="clickable" onClick={(e) => changeStep(e, 1)}>
                modify
              </span>{' '}
              to add more dates
            </p>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-evenly', flexDirection: 'row' }}>
        {showModifyButton && (
          <div>
            {showSingleDateWarning && (
              <div>
                <img src={arrow} style={{ width: 'auto', height: '27px', position: 'relative', left: '21px' }} />
              </div>
            )}
            <CustomButton color="warning" disabled={isSending} onClick={handleModifyClick}>
              Modify
            </CustomButton>
          </div>
        )}

        {showConfirmButton && (
          <div>
            {showSingleDateWarning && <div style={{ height: '27px' }} />}
            {!demo && (
              <CustomButton
                disabled={isSending}
                onClick={() => {
                  setIsSending(true);
                  sendRequest();
                }}
                color="success"
              >
                {!isSending ? 'Confirm' : <FontAwesomeIcon icon={faSpinner} spin />}
              </CustomButton>
            )}
            {demo && (
              <CustomButton disabled={isSending} onClick={simulateSending} color="success">
                Confirm
              </CustomButton>
            )}
          </div>
        )}
      </div>
    </Col>
  );
};

export default InterviewDateConfirm;
